// La ficha de un producto como está en Mercado Libre, para uno que está en las dos tiendas
// (ADR-2026-10-30-cada-tienda-con-su-ficha). Mercado Libre tiene sus propios datos —precio, estado, fotos,
// calidad, visitas, ventas— y se ven acá; lo que se edita (precio y descripción) va directo a la
// publicación con el bloque de siempre (`MercadoLibreFicha`).

import { useEffect, useState } from 'react';
import { ExternalLink, Eye, Gauge, Images, Pencil, ShoppingBag, Sparkles } from 'lucide-react';
import { MercadoLibreFicha } from '../../components/MercadoLibreFicha';
import { calidadDeMl, publicacionesDelProducto } from '../../lib/mercadoLibreDatos';
import { estadoLegible, formatoPrecio, type CalidadDeMl, type PublicacionDelProducto } from '../../lib/mercadoLibreDatosLogica';
import { describirPublicacionDeMl } from '../../lib/mercadoLibreFotos';
import { Seccion } from './Seccion';

export interface LecturaDeMl {
  publicaciones: PublicacionDelProducto[];
  calidad: CalidadDeMl | null;
  fotos: Array<{ id: string; url: string }>;
}

async function leer(productoId: string, brandId: string | null): Promise<LecturaDeMl> {
  const publicaciones = await publicacionesDelProducto(productoId);
  const principal = publicaciones[0];
  if (!principal) return { publicaciones, calidad: null, fotos: [] };
  const [calidad, pub] = await Promise.all([
    calidadDeMl(principal.item_id).catch(() => null),
    describirPublicacionDeMl(brandId, principal.item_id).catch(() => null),
  ]);
  return { publicaciones, calidad, fotos: pub?.imagenes ?? [] };
}

export function FichaDeMercadoLibre({
  productoId,
  companyId,
  brandId,
  sincroniza,
  onSincroniza,
}: {
  productoId: string;
  companyId: string | null;
  brandId: string | null;
  /** Los cambios de la ficha de Tiendanube se mandan también acá al sincronizar. */
  sincroniza: boolean;
  onSincroniza: (v: boolean) => void;
}) {
  const [lectura, setLectura] = useState<{ estado: 'cargando' } | { estado: 'error' } | ({ estado: 'listo' } & LecturaDeMl)>({
    estado: 'cargando',
  });
  useEffect(() => {
    let vivo = true;
    setLectura({ estado: 'cargando' });
    leer(productoId, brandId)
      .then((l) => vivo && setLectura({ estado: 'listo', ...l }))
      .catch(() => vivo && setLectura({ estado: 'error' }));
    return () => {
      vivo = false;
    };
  }, [productoId, brandId]);

  if (lectura.estado === 'cargando') {
    return (
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]" aria-busy="true">
        <div className="space-y-3">
          <div className="h-40 animate-pulse rounded-2xl bg-neutral-100" />
          <div className="h-56 animate-pulse rounded-2xl bg-neutral-100" />
        </div>
        <div className="h-64 animate-pulse rounded-2xl bg-neutral-100" />
      </div>
    );
  }
  if (lectura.estado === 'error' || lectura.publicaciones.length === 0) {
    return (
      <p role="alert" className="rounded-2xl bg-red-50 py-4 px-5 text-sm text-red-700">
        No pudimos leer la publicación de Mercado Libre. Probá de nuevo en un rato.
      </p>
    );
  }

  const { publicaciones, calidad, fotos } = lectura;
  const principal = publicaciones[0]!;
  const activa = principal.estado === 'active';

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
      <div className="space-y-3 lg:col-start-2 lg:row-start-1">
        {/* Cómo está la publicación hoy: lo que en Tiendanube no existe. */}
        <div className="rounded-2xl border border-neutral-200 bg-surface-card p-5">
          <div className="flex items-start justify-between gap-3">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full py-1 px-2.5 text-xs font-medium ${
                activa ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-600'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${activa ? 'bg-emerald-500' : 'bg-neutral-400'}`} aria-hidden="true" />
              {estadoLegible(principal.estado)}
            </span>
            {principal.permalink && (
              <a
                href={principal.permalink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:opacity-70"
              >
                Ver publicación <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
          <p className="mt-3 text-2xl font-semibold tabular-nums text-brand-black">{formatoPrecio(principal.precio, principal.moneda)}</p>
          {principal.titulo && <p className="mt-1 line-clamp-2 text-sm text-neutral-500">{principal.titulo}</p>}
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Dato icono={<ShoppingBag className="h-4 w-4" />} valor={principal.vendidos} texto="vendidos" />
            <Dato icono={<Eye className="h-4 w-4" />} valor={principal.visitas_30d} texto="visitas en 30 días" />
          </div>
        </div>

        {calidad && <TarjetaDeCalidad calidad={calidad} />}

        <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl border border-neutral-200 bg-surface-card p-4">
          <span className="text-sm text-neutral-600">
            <span className="block font-medium text-brand-black">Sincronizar desde Tiendanube</span>
            Los cambios de la ficha de Tiendanube también se mandan acá.
          </span>
          <input type="checkbox" role="switch" checked={sincroniza} onChange={(e) => onSincroniza(e.target.checked)} className="peer sr-only" />
          <span
            aria-hidden="true"
            className="relative h-6 w-11 shrink-0 rounded-full bg-neutral-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-brand-primary peer-checked:after:translate-x-5"
          />
        </label>
      </div>

      <div className="min-w-0 space-y-3 lg:col-start-1 lg:row-start-1">
        <Seccion id="ml-fotos" titulo="Fotos" icono={<Images className="h-4 w-4" />} plegable={false} resumen={`${fotos.length}`}>
          {fotos.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {fotos.map((f) => (
                <img key={f.id} src={f.url} alt="" loading="lazy" className="h-28 w-28 shrink-0 rounded-xl object-cover shadow-sm" />
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-500">Esta publicación no tiene fotos.</p>
          )}
        </Seccion>
        <Seccion id="ml-editar" titulo="Precio y descripción" icono={<Pencil className="h-4 w-4" />} plegable={false}>
          <p className="mb-3 text-xs text-neutral-500">Se cambian directo en Mercado Libre, sin sincronizar.</p>
          <MercadoLibreFicha companyId={companyId} publicaciones={publicaciones} soloEdicion />
        </Seccion>
      </div>
    </div>
  );
}

function Dato({ icono, valor, texto }: { icono: React.ReactNode; valor: number | null; texto: string }) {
  return (
    <div className="rounded-xl bg-neutral-50 py-2.5 px-3">
      <span className="flex items-center gap-1.5 text-neutral-400">{icono}</span>
      <p className="mt-1 text-lg font-semibold tabular-nums text-brand-black">{valor === null ? '—' : valor.toLocaleString('es-AR')}</p>
      <p className="text-xs text-neutral-500">{texto}</p>
    </div>
  );
}

function TarjetaDeCalidad({ calidad }: { calidad: CalidadDeMl }) {
  const puntaje = calidad.sin_calcular ? null : calidad.puntaje;
  const color = puntaje === null ? 'bg-neutral-300' : puntaje >= 80 ? 'bg-emerald-500' : puntaje >= 50 ? 'bg-amber-400' : 'bg-red-500';
  return (
    <div className="rounded-2xl border border-neutral-200 bg-surface-card p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-brand-black">
          <Gauge className="h-4 w-4 text-neutral-400" />
          Calidad de la publicación
        </p>
        {puntaje !== null && <span className="text-sm font-semibold tabular-nums text-brand-black">{puntaje}/100</span>}
      </div>
      {puntaje === null ? (
        <p className="mt-2 text-xs text-neutral-500">Mercado Libre todavía no la calculó.</p>
      ) : (
        <>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-neutral-100">
            <div className={`h-full rounded-full ${color} transition-[width] duration-500`} style={{ width: `${puntaje}%` }} />
          </div>
          {calidad.nivel && <p className="mt-1.5 text-xs text-neutral-500">{calidad.nivel}</p>}
        </>
      )}
      {calidad.recomendaciones.length > 0 && (
        <ul className="mt-4 space-y-2 border-t border-neutral-100 pt-3">
          {calidad.recomendaciones.slice(0, 4).map((r) => (
            <li key={r.titulo} className="flex items-start gap-2 text-xs text-neutral-600">
              <Sparkles className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${r.modo === 'advertencia' ? 'text-amber-500' : 'text-brand-primary'}`} />
              {r.link ? (
                <a href={r.link} target="_blank" rel="noreferrer" className="hover:underline">
                  {r.titulo}
                </a>
              ) : (
                r.titulo
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
